import { Dispatch, SetStateAction, createContext, useCallback, useContext, useEffect, useState } from "react";
import styles from "./BundlesHomePage.module.css";
import {
    Badge,
    Banner,
    Button,
    Card,
    Link,
    SkeletonBodyText,
    SkeletonDisplayText,
    SkeletonThumbnail,
    Tabs,
} from "@shopify/polaris";
import { Route, Routes, useNavigate, useParams } from "react-router-dom";
import { getBundleRoute } from "../constants";
import useBundleMixpanelHook from "../../hooks/useBundleMixpanelHook";
import { BiLinkExternal } from "react-icons/bi";
import Support from "../../components/Icons/Support";
import LimitReachedPopup from "../../components/Plans/LimitReachedPopup";
import { CurrentPlan, PLANS } from "../../components/Plans/plansConstants";
import { shouldShowPopupSession } from "../../utilities/constants";
import { AppConfigContext } from "../../context/AppConfigContext";
import {
    AiCards,
    BundleBuilderCards,
    BundleCards,
    CardType,
    CartCards,
    PostPurchaseCards,
    ThankYouPageCards,
} from "./HomePageConstants";
import RevenueCard from "./RevenueCard";
import LockIcon from "../../components/Icons/LockIcon";
import { BundleTypes } from "./constants";
import { getData, postData, endPoints } from "../../utilities/networkCalls";
import { lazyWithErrorHandling } from "../../utilities/functions";

// Lazy load all route components
const Standard = lazyWithErrorHandling(() => import("./Standard/Standard"));
const VolumeDiscount = lazyWithErrorHandling(() => import("./VolumeDiscount/VolumeDiscount"));
const Bxgy = lazyWithErrorHandling(() => import("./Bxgy/Bxgy"));
const MixMatch = lazyWithErrorHandling(() => import("./MixMatch/MixMatch"));
const MixMatchSectional = lazyWithErrorHandling(() => import("./MixMatchSectional/MixMatchSectional"));
const FBT = lazyWithErrorHandling(() => import("./FTB/FBT"));
const ProductAddons = lazyWithErrorHandling(() => import("./productAddOns/ProductAddons"));
const ProductAddonsPopup = lazyWithErrorHandling(() => import("./productAddOnsPopup/ProductAddonsPopup"));
const CartDrawerAdmin = lazyWithErrorHandling(() => import("../Cart/CartDrawerAdmin/CartDrawerAdmin"));
const PostPurchase = lazyWithErrorHandling(() => import("./PostPurchase/PostPurchase"));
const ThankYouPage = lazyWithErrorHandling(() => import("./ThankYouPage/ThankYouPage"));
const AiBundlesWrapper = lazyWithErrorHandling(() => import("./AiBundles/AiBundlesWrapper"));
const BuyN = lazyWithErrorHandling(() => import("./BuyN/BuyN"));

import useAIBundleMixpanelHook from "../../hooks/useAIBundleMixpanelHook";
import AutocompleteSearch, { Section } from "../../components/AutocompleteSearch/AutocompleteSearch";
import Onboarding from "../../components/Onboarding/Onboarding";
import { useShowOnboarding } from "../../context/ShowOnboardingContext";
import ExtensionAndEmbed from "../../components/ExtensionAndEmbed/ExtensionAndEmbed";
import Placeholder from "./Placeholder/Placeholder";
import BundleBuilder from "./BundleBuilder/BundleBuilder";
import NpsBanner from "./Nps/NpsBanner";

const POLLING_INTERVAL = 15000;
const MAX_RETRIES = 20;
interface AppsContextType {
    setShowCatalog: Dispatch<SetStateAction<boolean>>;
    publishCount: null | number;
    setPublishCount: Dispatch<SetStateAction<null | number>>;
}

const getOptionsByTab = (tabId: string): Section[] => {
    switch (tabId) {
        case "all":
            return [
                {
                    title: "Frequently used",
                    options: [
                        { value: "ai_bundle", label: "AI Bundle" },
                        { value: "standard_bundle", label: "Standard Bundle" },
                        { value: "volume_bundle", label: "Quantity Breaks" },
                        { value: "slide_cart", label: "Slide Cart" },
                    ],
                },
            ];
        case "bundles":
            return [
                {
                    title: "Frequently used",
                    options: [
                        { value: "standard_bundle", label: "Standard Bundle" },
                        { value: "volume_bundle", label: "Quantity Breaks" },
                    ],
                },
            ];
        case "bundles-builders":
            return [
                {
                    title: "Frequently used",
                    options: [
                        { value: "ai_bundle", label: "Mix & Match (Sectional)" },
                        { value: "bundle_builder", label: "Build your own box" },
                    ],
                },
            ];
        case "cart-drawer":
        case "thank-you-page":
        case "post-purchase":
            return [];
        default:
            return [];
    }
};

export const AppsContext = createContext<AppsContextType>({} as AppsContextType);

const CalendlyBanner = () => {
    const { trackBookSession } = useBundleMixpanelHook();
    return (
        <div className={styles.chatCalendlyBanner}>
            <div className={styles.chatCalendlyBannerLeft}>
                <div className={styles.chatCalendlyBannerTagline}>Boost your success with Kefi Bundles on Shopify!</div>
                <div className={styles.chatCalendlyBannerSub}>
                    Sign up for a free 30-minute setup session and learn how to create compelling product bundles. Our
                    experts will ensure a smooth onboarding process and help you achieve your business goals.
                </div>
                <div className={styles.chatCalendlyBannerCtas}>
                    <Button
                        onClick={() => {
                            trackBookSession({
                                cta: "Book a setup session",
                            });
                            window.open("https://calendly.com/wecare-keficommerce/15min", "_blank");
                        }}
                    >
                        Book a setup session
                    </Button>
                    <Button
                        variant={"tertiary"}
                        onClick={() => {
                            trackBookSession({
                                cta: "Talk to support now",
                            });
                            window.userengage("widget.open");
                        }}
                    >
                        Talk to support now
                    </Button>
                </div>
            </div>
            <div className={styles.chatCalendlyBannerRight}>
                <Support />
            </div>
        </div>
    );
};
typeof CalendlyBanner;

const BundleCardShimmer = () => {
    return (
        <Card roundedAbove="sm">
            <div className={styles.appCard} style={{ height: 300 }}>
                <div className={styles.appCard_left} style={{ marginBottom: 10 }}>
                    <SkeletonThumbnail size={"large"} />
                </div>
                <div className={styles.appCard_right} style={{ minWidth: "80%" }}>
                    <div className={styles.appCard_title} style={{ gap: 30, paddingInline: 0 }}>
                        <div style={{ width: "80%" }}>
                            <SkeletonBodyText lines={2} />
                        </div>
                    </div>
                    <div
                        style={{
                            width: "100%",
                            marginTop: 20,
                            marginRight: "auto",
                        }}
                    >
                        <SkeletonBodyText lines={3} />
                    </div>
                </div>
            </div>
        </Card>
    );
};

const BundleCard = ({
    item,
    source = "",
    setShowCatalog,
    redirectToPlan,
}: {
    item: any;
    source: string;
    setShowCatalog?: any;
    redirectToPlan?: boolean;
}) => {
    const goTo = useNavigate();
    const { trackCreateBundles } = useBundleMixpanelHook();
    const [showMore, setShowMore] = useState(false);

    const handleSelect = () => {
        if (redirectToPlan) {
            goTo("/billing");
            return;
        } else {
            if (!item?.tag) {
                trackCreateBundles({
                    cta: "Create Bundle",
                    ctaType: source || "",
                    bundleType: item?.bundle_type,
                });
                goTo(getBundleRoute(item?.bundle_type));
                setShowCatalog(false);
            }
        }
    };
    return (
        <div className={styles.appCardParent}>
            <div className={styles.appCard}>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                    }}
                >
                    {!showMore && <div className={styles.appCard_top}>{item.icon}</div>}
                    {!showMore && item?.specialTag && (
                        <div className={styles.appCard_specialTag}>
                            <Badge tone={"attention"}>{item?.specialTag}</Badge>
                        </div>
                    )}
                    <div className={styles.appCard_title}>{item.label}</div>
                    <div
                        className={`${styles.appCard_desc} ${showMore ? styles.limitMaxHeight : styles.limitMinHeight}`}
                    >
                        <div className={styles.useCaseSection}>
                            Use Cases<span className={styles.useCaseSepa}></span>
                        </div>
                        <div className={`${styles.useCasesList} `}>
                            <div
                                style={{
                                    maxHeight: showMore ? "none" : "6rem",
                                    overflow: "hidden",
                                    transition: "max-height 0.2s ease-in-out",
                                }}
                            >
                                <ul
                                    style={{
                                        listStyleType: "disc",
                                        paddingLeft: "1.5em",
                                        margin: 0,
                                    }}
                                >
                                    {item?.useCases?.map((uc: any, idx: number) => (
                                        <li
                                            key={idx}
                                            style={{
                                                lineHeight: "18px",
                                                marginBottom: "2px",
                                            }}
                                        >
                                            {uc}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                        {item?.min < item?.useCases?.length && (
                            <span style={{ marginTop: -4 }}>
                                <Button
                                    variant={"plain"}
                                    onClick={() => {
                                        setShowMore((ps) => !ps);
                                    }}
                                    disclosure={showMore ? "up" : "down"}
                                >
                                    {showMore ? "Show Less" : "Show more"}
                                </Button>
                            </span>
                        )}
                    </div>
                </div>
                {!showMore && (
                    <div className={styles.cardsBottomCtaRow}>
                        <Button
                            variant={"secondary"}
                            size="large"
                            onClick={handleSelect}
                            disabled={item?.bundle_type === BundleTypes.BXGY_SECTIONAL}
                        >
                            {item?.bundle_type === BundleTypes.BXGY_SECTIONAL ? "Coming Soon" : "Select"}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

const CartCard = ({
    item,
    source = "",
    redirectToPlan,
    currentPlan,
    forAi = false,
    checkAiBundlesCreationStatus = () => {},
    trackGetAIBundle = () => {},
    loadingAiBundles = false,
}: {
    item: any;
    source: string;
    redirectToPlan?: boolean;
    currentPlan: CurrentPlan;
    forAi?: boolean;
    checkAiBundlesCreationStatus?: any;
    trackGetAIBundle?: any;
    loadingAiBundles?: boolean;
}) => {
    const goTo = useNavigate();
    const { trackCreateBundles } = useBundleMixpanelHook();
    const [showMore, setShowMore] = useState(false);
    const [isLocked, setIsLocked] = useState(false);

    useEffect(() => {
        if (currentPlan?.plan_id != null) {
            setIsLocked(
                ![
                    PLANS.FREE_NEW,
                    PLANS.NEW_PLAN,
                    PLANS.STARTER,
                    PLANS.UNLIMITED,
                    PLANS.CUSTOM_PLAN,
                    PLANS.BASIC_ANNUAL,
                    PLANS.STARTER_ANNUAL,
                    PLANS.UNLIMITED_ANNUAL,
                ].includes(currentPlan?.plan_id),
            );
        } else {
            setIsLocked(false);
        }
    }, [currentPlan]);

    const handleSelect = () => {
        if (redirectToPlan || isLocked) {
            goTo("/billing");
            return;
        } else {
            if (forAi) {
                trackGetAIBundle({
                    cta: "AI Bundles Card",
                    ctaType: "Homepage",
                });
                checkAiBundlesCreationStatus();
            } else {
                if (!item?.tag) {
                    trackCreateBundles({
                        cta: "Create Bundle",
                        ctaType: source || "",
                        bundleType: item?.bundle_type,
                    });
                    goTo(getBundleRoute(item?.bundle_type));
                }
            }
        }
    };

    return (
        <div className={styles.cartCardParent}>
            <div
                style={{
                    visibility: item?.bundle_type ? "visible" : "hidden",
                }}
                className={`${styles.cartCard} ${isLocked && styles.lockedCard}`}
            >
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        width: "100%",
                    }}
                >
                    {isLocked && (
                        <div className={styles.lockCardHover}>
                            <div className={styles.lockedCardHoverIconWrapper}>
                                <LockIcon size={27} />
                            </div>
                            <div className={styles.lockedCardHoverText}>Choose new plans to unlock this feature.</div>
                        </div>
                    )}
                    {item?.bundle_type && (
                        <>
                            {!showMore && <div className={styles.appCard_top}>{item.icon}</div>}
                            {!showMore && item?.specialTag && (
                                <div className={styles.appCard_specialTag}>
                                    <Badge tone={"attention"}>{item?.specialTag}</Badge>
                                </div>
                            )}
                            <div className={styles.appCard_title}>
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "flex-start",
                                        alignItems: "baseline",
                                        gap: 10,
                                    }}
                                >
                                    {item.label}
                                    {isLocked && (
                                        <span
                                            style={{ position: "relative", bottom: -2 }}
                                            className={styles.lockIconWrapper}
                                        >
                                            <LockIcon />
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className={styles.appCard_desc}>
                                <div className={styles.useCaseSection}>
                                    {item.bundle_type == BundleTypes.BB_PLACEHOLDER ? "Use Cases" : "Features"}
                                    <span className={styles.useCaseSepa}></span>
                                </div>
                                <div className={`${styles.useCasesList}`}>
                                    <div
                                        style={{
                                            maxHeight: showMore ? "none" : "6em",
                                            overflow: "hidden",
                                            transition: "max-height 0.2s ease-in-out",
                                        }}
                                    >
                                        {item?.useCases?.map((uc: any, idx: number) => {
                                            return (!showMore && idx < item?.min) || showMore ? (
                                                <ul
                                                    style={{
                                                        listStyleType: "disc",
                                                        paddingLeft: "1.5em",
                                                        margin: 0,
                                                    }}
                                                >
                                                    <li
                                                        key={idx}
                                                        style={{
                                                            lineHeight: "18px",
                                                            marginBottom: "2px",
                                                        }}
                                                    >
                                                        {uc}
                                                    </li>
                                                </ul>
                                            ) : (
                                                <></>
                                            );
                                        })}
                                    </div>
                                </div>

                                {item?.min < item?.useCases?.length && (
                                    <span style={{ marginTop: -4 }}>
                                        <Button
                                            variant={"plain"}
                                            onClick={() => {
                                                setShowMore((ps) => !ps);
                                            }}
                                            disclosure={showMore ? "up" : "down"}
                                        >
                                            {showMore ? "Show Less" : "Show more"}
                                        </Button>
                                    </span>
                                )}
                            </div>
                        </>
                    )}
                </div>
                <div className={styles.cardsBottomCtaRow}>
                    <Button
                        variant={"secondary"}
                        size="large"
                        disabled={loadingAiBundles}
                        loading={loadingAiBundles}
                        onClick={handleSelect}
                    >
                        Select
                    </Button>
                </div>
            </div>
        </div>
    );
};

const ProductCatalogPage = ({
    source = "",
    setShowCatalog,
    redirectToPlan = false,
    currentPlan,
    tab,
    filteredBundleCards,
    filteredCartCards = [],
}: {
    redirectToPlan: boolean;
    source: string;
    setShowCatalog: any;
    currentPlan: any;
    tab: string;
    trackGetAIBundle?: any;
    checkAiBundlesCreationStatus?: any;
    loadingAiBundles?: boolean;
    filteredBundleCards: CardType[];
    filteredAICards?: CardType[];
    filteredCartCards?: CardType[];
}) => {
    if (!filteredBundleCards.length) return null;

    if (tab === "all") {
        const firstTwo = filteredBundleCards.slice(0, 2); // filter out empty cards
        const remaining = filteredBundleCards.slice(2);
        return (
            <>
                {firstTwo
                    .filter((cd) => cd?.label)
                    .map((cd) => (
                        <BundleCard
                            key={cd.bundle_type}
                            item={cd}
                            source={source}
                            setShowCatalog={setShowCatalog}
                            redirectToPlan={redirectToPlan}
                        />
                    ))}

                {filteredCartCards?.length > 0 &&
                    filteredCartCards
                        ?.slice(0, 1)
                        .filter((cc) => cc?.label)
                        .map((cc) => (
                            <CartCard
                                item={cc}
                                key={cc.bundle_type}
                                source={source}
                                currentPlan={currentPlan}
                                redirectToPlan={redirectToPlan}
                            />
                        ))}

                {remaining
                    ?.filter((cd) => cd?.label)
                    .map((cd) => {
                        return cd?.label ? (
                            <BundleCard
                                key={cd.bundle_type}
                                item={cd}
                                source={source}
                                setShowCatalog={setShowCatalog}
                                redirectToPlan={redirectToPlan}
                            />
                        ) : null;
                    })}
            </>
        );
    }

    return (
        <>
            {filteredBundleCards.map((cd) => {
                return cd?.label ? (
                    <BundleCard
                        key={cd.bundle_type}
                        item={cd}
                        source={source}
                        setShowCatalog={setShowCatalog}
                        redirectToPlan={redirectToPlan}
                    />
                ) : (
                    <div style={{ visibility: "hidden" }}>Empty</div>
                );
            })}
        </>
    );
};

const BundlesHomePage = () => {
    const [showCatalog, setShowCatalog] = useState(false);
    const { plansInfo, subscription_info, trial_status } = useContext(AppConfigContext);
    const params = useParams();

    const goTo = useNavigate();

    const [TABS] = useState([
        {
            content: "All",
            panelID: "all",
            id: "all",
            status: false,
            index: 0,
            route: "all",
        },
        {
            content: "Bundle Builder",
            panelID: "bundles-builder",
            id: "bundles-builders",
            status: false,
            index: 1,
            route: "bundle-builder-tab",
        },
        {
            content: "Product Page",
            panelID: "bundles",
            id: "bundles",
            status: false,
            index: 2,
            route: "product-page-tab",
        },

        {
            content: "Slide Cart",
            panelID: "cart-drawer",
            id: "cart-drawer",
            status: false,
            index: 3,
            route: "slide-cart-tab",
        },
        { content: "Post Purchase", panelID: "post-purchase", id: "post-purchase", status: false, index: 4 },
        {
            content: "Thank You Page",
            panelID: "thank-you-page",
            id: "thank-you-page",
            status: false,
            index: 5,
            route: "thank-you-page-tab",
        },
    ]);

    const [activeTab, setActiveTab] = useState(TABS[0]);

    const handleTabChange = useCallback((selectedTabIndex: number) => {
        setActiveTab(TABS[selectedTabIndex]);
    }, []);

    const { isExhausted, usagePercent, currentOrders, isTrial } = plansInfo;

    const [statCards, setStatCards] = useState({
        revenue: "0",
        conversions: "0",
        aov: "0",
    });
    const [loadingStats, setLoadingStats] = useState(true);
    const [showLimitPopup, setShowLimitPopup] = useState(false);
    const [loadingBundles, setLoadingBundles] = useState(false);
    const [generatingAiBundles, setGeneratingAiBundles] = useState(false);
    const [loadingCurrentPlan, setLoadingCurrentPlan] = useState(true);
    const [currentPlan, setCurrentPlan] = useState<{ plan_id: number; plan_name: string } | any>({ plan_id: 99 });
    const { trackHomepageLanding } = useBundleMixpanelHook();
    const { trackGetAIBundle } = useAIBundleMixpanelHook();
    const [searchTerm, setSearchTerm] = useState<string>("");

    // filteration for all cards
    const filterCards = (allCards: any[], searchTerm: string) => {
        if (!searchTerm) return allCards;
        const searchLower = searchTerm.toLowerCase();

        const matchingCards = allCards.filter((card) => {
            if (!card.search_tags || card.search_tags.length === 0) return true;
            return card.search_tags.some((tag: string) => tag.toLowerCase().includes(searchLower));
        });

        const remainingCount = allCards.length - matchingCards.length;
        const paddedCards = [...matchingCards, ...Array(remainingCount).fill({})];

        return paddedCards;
    };

    const filteredBundleCards = filterCards(BundleCards, searchTerm);
    const filteredBundleBuilderCards = filterCards(BundleBuilderCards, searchTerm);
    const filteredAICards = filterCards(AiCards, searchTerm);
    const filteredCartCards = filterCards(CartCards, searchTerm);
    const filteredPostPurchaseCards = filterCards(PostPurchaseCards, searchTerm);
    const filteredThankYouCards = filterCards(ThankYouPageCards, searchTerm);
    const totalVisibleCardsCount =
        filteredBundleCards?.filter((cc) => cc?.label).length +
        filteredBundleBuilderCards?.filter((cc) => cc?.label).length +
        filteredAICards?.filter((cc) => cc?.label).length +
        filteredCartCards?.filter((cc) => cc?.label).length +
        filteredPostPurchaseCards?.filter((cc) => cc?.label).length +
        filteredThankYouCards?.filter((cc) => cc?.label).length;

    useEffect(() => {
        if (params?.tab) {
            const tab = TABS.find((t) => t.route === params.tab);
            if (tab) {
                setActiveTab(tab);

                const interval = setInterval(() => {
                    const element = document.getElementById("catalogueStarting");

                    if (element) {
                        element.scrollIntoView({ behavior: "smooth", block: "start" });
                        clearInterval(interval);
                    }
                }, 500);
            } else {
                setActiveTab(TABS[0]);
            }
        }
    }, []);

    useEffect(() => {
        if ([PLANS.FREE_OLD, PLANS.NEW_PLAN, PLANS.FREE_NEW, PLANS.STARTER].includes(currentPlan?.plan_id)) {
            if (usagePercent >= 100 && !isTrial) {
                setShowLimitPopup(true);
            } else if (!isTrial && usagePercent >= 80 && shouldShowPopupSession("limit80")) {
                setShowLimitPopup(true);
            } else {
                setShowLimitPopup(false);
            }
        }
        if (window.localStorage.getItem("redirectToPlans") == "1") window.localStorage.setItem("redirectToPlans", "0");
    }, [usagePercent]);

    useEffect(() => {
        getBundlesStats();
        getCurrentPlan();
    }, []);

    useEffect(() => {
        if (currentPlan?.plan_id !== 99) {
            trackHomepageLanding({
                cta: "HomePage Landing",
                freshUser: currentPlan == null ? "Fresh" : ("Old" as any),
                bundleCount: "-" as any,
            });
        }

        // if (currentPlan === null || Object.keys(currentPlan).length === 0) {
        //     handleRedirectToPlan();
        //     return;
        // }
    }, [currentPlan]);

    // const handleRedirectToPlan = () => {
    //     goTo("/billing");
    // };

    const getCurrentPlan = async () => {
        setLoadingCurrentPlan(true);
        const res = await getData({ url: endPoints.getCurrentPlan });
        if (res.status == 200) {
            setCurrentPlan(res?.data?.currentPlan?.plan_id ? res?.data?.currentPlan : null);
            setGetDiscountStatus(res?.data?.discountStatus ?? 1);
        } else {
            setCurrentPlan(null);
            setGetDiscountStatus(1);
        }
        setLoadingCurrentPlan(false);
    };

    const getBundlesStats = async (refresh: boolean = false) => {
        if (!refresh) setLoadingStats(true);

        const offerTypes: string[] = ["bundles", "slide_cart", "post_purchase", "thank_you"];

        const res = await getData({
            url: endPoints.summaryKpis,
            params: {
                offer_types: offerTypes,
            },
        });
        if (res.status == 200) {
            const revenue = (Math.round((res?.data?.total_revenue || 0) * 100) / 100).toString();
            const conversions = res?.data?.total_orders || "0";
            const aov = res?.data?.aov || "0";

            setStatCards({
                revenue,
                conversions,
                aov,
            });
        } else {
            setStatCards({
                revenue: "0",
                conversions: "0",
                aov: "0",
            });
        }

        setLoadingStats(false);
    };

    const getAllBundles = async (generateNew = false) => {
        setLoadingBundles(true);
        const res = await getData({
            url: endPoints.getAllBundles + `?search=&status=&sort_by=status&order=asc&cat=`,
        });
        if (res.status == 200) {
            setMaxProductCount(res?.data?.maxProductCount ?? 0);
            if (res?.data?.allBundles?.length) {
                const aiBuns = res.data?.allBundles?.filter((bun: any) => bun?.ai_bundle_status && !bun?.status) ?? [];
                if (aiBuns?.length == 0 && generateNew) {
                    handleGenerateAiBundle();
                    return;
                }
                setAiBundles(aiBuns);
            } else {
                setLoadingBundles(false);
                setAiBundles([]);
            }
            setLoadingBundles(false);
        }
    };

    const [aiBundles, setAiBundles] = useState<any[] | []>([]);
    const [maxProductCount, setMaxProductCount] = useState<number>(0);
    const [isAiBundlesEnabled, setIsAiBundlesEnabled] = useState(true);
    console._log(setIsAiBundlesEnabled);
    const [publishCount, setPublishCount] = useState<null | number>(null);

    useEffect(() => {
        const planState = window.localStorage.getItem("plan_cta");
        if (planState && planState != "-") {
            shopify.toast.show(planState == "new" ? "Plan selected successfully" : "Plan updated successfully", {
                isError: false,
            });
            window.localStorage.removeItem("plan_cta");
        }
    }, []);

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0 });
    }, [showCatalog]);

    const [getDiscountStatus, setGetDiscountStatus] = useState<number>(1);

    const checkAiBundlesCreationStatus = async () => {
        setLoadingBundles(true);
        const res = await getData({ url: endPoints.aiBundlesCreationStatus });
        if (res?.status == 200) {
            setLoadingBundles(false);
            if (res?.data?.aiBundleApiStatus) {
                getAllBundles(true);
            } else {
                setGeneratingAiBundles(true);
                setTimeout(() => {
                    pollUntilStatusIsFalse(0);
                }, POLLING_INTERVAL);
            }
            goTo(getBundleRoute(BundleTypes.AI_BUNDLES));
        } else {
            setLoadingBundles(false);
        }
    };

    const pollUntilStatusIsFalse = async (retryCount: number) => {
        const res = await getData({ url: endPoints.aiBundlesCreationStatus });
        if (res?.status === 200) {
            const isStillProcessing = !res.data?.aiBundleApiStatus;

            if (isStillProcessing) {
                // Keep polling
                if (retryCount < MAX_RETRIES) {
                    setTimeout(() => {
                        pollUntilStatusIsFalse(retryCount + 1);
                    }, POLLING_INTERVAL);
                } else {
                    setGeneratingAiBundles(false);
                }
            } else {
                getAllBundles(false);
                setGeneratingAiBundles(false);
                goTo(getBundleRoute(BundleTypes.AI_BUNDLES));
            }
        } else {
            setGeneratingAiBundles(false);
        }
    };

    const handleGenerateAiBundle = async () => {
        setGeneratingAiBundles(true);
        const res = await postData({ url: endPoints.createAiBundles, payload: {} });
        if (res?.status === 200) {
            setGeneratingAiBundles(true);
            setTimeout(() => {
                pollUntilStatusIsFalse(0);
            }, POLLING_INTERVAL);
        } else {
            setGeneratingAiBundles(false);
        }
    };

    const userShopname = shopify?.config?.shop?.split(".")[0];
    const { showOnboarding } = useShowOnboarding();

    return (
        <AppsContext.Provider value={{ setShowCatalog, publishCount, setPublishCount }}>
            <div style={{ width: "100%", margin: "auto" }}>
                <>
                    <Routes>
                        <Route path={"/standard/:bId?"} element={<Standard />} />
                        <Route path={"/quantity-break/:bId?"} element={<VolumeDiscount />} />
                        <Route path={"/bxgy/:bId?"} element={<Bxgy />} />
                        <Route path={"/mixmatch/:bId?"} element={<MixMatch />} />
                        <Route path={"/buy-n/:bId?"} element={<BuyN />} />
                        <Route path={"/mixmatch-sectional/:bId?"} element={<MixMatchSectional />} />
                        <Route path={"/fbt/:bId?"} element={<FBT />} />
                        <Route path={"/bundle-builder/byob/:bId?"} element={<BundleBuilder />} />
                        <Route path={"/bundle-builder/placeholder/:bId?"} element={<Placeholder />} />
                        <Route path={"/product_add_on/:bId?"} element={<ProductAddons />} />
                        <Route path={"/product_add_on_popup/:bId?"} element={<ProductAddonsPopup />} />
                        <Route path={"/slide-cart/:bId?"} element={<CartDrawerAdmin />} />
                        <Route path={"/post-purchase/:ppId?"} element={<PostPurchase />} />
                        <Route path={"/thank-you-page/:bId?"} element={<ThankYouPage />} />
                        <Route
                            path={"/ai-bundles/:bId?"}
                            element={
                                <AiBundlesWrapper
                                    aiBundles={aiBundles}
                                    isAccessible={isAiBundlesEnabled}
                                    loadingBundles={loadingBundles}
                                    setLoadingBundles={setLoadingBundles}
                                    checkAiBundlesCreationStatus={checkAiBundlesCreationStatus}
                                    handleGenerateAiBundle={handleGenerateAiBundle}
                                    getAllBundles={getAllBundles}
                                    generatingAiBundles={generatingAiBundles}
                                    maxProductCount={maxProductCount}
                                    noPlan={currentPlan == null}
                                />
                            }
                        />
                        <Route
                            path={"/*"}
                            element={
                                <div className={styles.homePageWrapper}>
                                    <NpsBanner />

                                    {showOnboarding == "1" ? (
                                        <Onboarding
                                            currentPlanExists={subscription_info?.plan_id ? true : false}
                                            shouldShowTrial={trial_status}
                                        />
                                    ) : null}

                                    {/* {!(currentPlan == null) && <HowTo />} */}
                                    {showLimitPopup && (
                                        <LimitReachedPopup
                                            setPopup={setShowLimitPopup}
                                            isExhausted={isExhausted}
                                            currentPlan={currentPlan}
                                            currentOrders={currentOrders}
                                        />
                                    )}

                                    {/* {currentPlan != null && showOnboarding == "0" && (
                                        <EnableExtensionModal
                                            setCanShowExpiry={setCanShowExpiry}
                                            appType={"Bundles & Slide Cart"}
                                        />
                                    )} */}

                                    {currentPlan != null && <ExtensionAndEmbed />}

                                    {loadingCurrentPlan && (
                                        <>
                                            <div>
                                                <SkeletonDisplayText maxWidth={"100%"} />
                                                <SkeletonDisplayText maxWidth={"100%"} />
                                            </div>
                                            <div
                                                className={styles.appsCard}
                                                style={{
                                                    marginTop: "20px",
                                                }}
                                            >
                                                {BundleCards.length &&
                                                    BundleCards.map((cd) => <BundleCardShimmer key={cd.bundle_type} />)}
                                            </div>
                                        </>
                                    )}

                                    {!loadingStats && !loadingCurrentPlan && (
                                        <>
                                            {getDiscountStatus === 0 && (
                                                <Banner title="Action Required" tone="warning">
                                                    The "Kefi Bundles" discount is not found. The discount will no
                                                    longer apply at checkout. Please create a new bundle to generate a
                                                    new discount.
                                                </Banner>
                                            )}
                                            {getDiscountStatus === 2 && (
                                                <Banner title="Action Required" tone="warning">
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            gap: "10px",
                                                        }}
                                                    >
                                                        <p>
                                                            The discount will be applied at checkout only when the "Kefi
                                                            Bundles" discount is active.
                                                        </p>
                                                        <Link
                                                            url={`https://admin.shopify.com/store/${userShopname}/discounts`}
                                                            target="_blank"
                                                        >
                                                            <Button
                                                                variant="primary"
                                                                icon={
                                                                    <BiLinkExternal
                                                                        size="16px"
                                                                        style={{
                                                                            marginBottom: "-3px",
                                                                        }}
                                                                    />
                                                                }
                                                            >
                                                                Activate Now!
                                                            </Button>
                                                        </Link>
                                                    </div>
                                                </Banner>
                                            )}

                                            {currentPlan == null ? (
                                                // <RedirectToPlans />
                                                <></>
                                            ) : (
                                                <RevenueCard statCards={statCards} loading={loadingStats} />
                                            )}

                                            <div
                                                style={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    gap: "6px",
                                                    marginTop: "10px",
                                                }}
                                            >
                                                <div className={styles.revenueCardLabel} id="catalogueStarting">
                                                    Configure Offers Here and Boost Your Revenue
                                                </div>

                                                <div className={styles.productsSelectorWrapper}>
                                                    <div className={styles.tabsContainer}>
                                                        <Tabs
                                                            tabs={TABS}
                                                            selected={activeTab?.index || 0}
                                                            onSelect={handleTabChange}
                                                        ></Tabs>
                                                    </div>
                                                    {/* Autocomplete search bar */}
                                                    <AutocompleteSearch
                                                        deselectedOptions={getOptionsByTab(activeTab.id)}
                                                        searchTerm={searchTerm}
                                                        setSearchTerm={setSearchTerm}
                                                        activeTab={activeTab}
                                                        actionBeforeProps={{
                                                            accessibilityLabel: "Bundle Builder action",
                                                            badge: {
                                                                tone: "new",
                                                                content: "New!",
                                                            },
                                                            helpText: "Bundle Builder",
                                                            ellipsis: true,
                                                            // helpText: "Bundle Builder",
                                                            onAction: () => {
                                                                goTo(getBundleRoute(BundleTypes.BUNDLE_BUILDER));
                                                            },
                                                        }}
                                                    />
                                                </div>

                                                <div className={styles.productsSelectorHeaderContent}>
                                                    {activeTab.id == TABS[0].id &&
                                                        (totalVisibleCardsCount > 0 ? (
                                                            <div className={styles.appsCard}>
                                                                {filteredBundleBuilderCards.length > 0 &&
                                                                    filteredBundleBuilderCards
                                                                        .slice(0, 4)
                                                                        .filter((cc) => cc?.label)
                                                                        .map((cc) => (
                                                                            <BundleCard
                                                                                item={cc}
                                                                                key={cc.bundle_type}
                                                                                source={"Homepage"}
                                                                                setShowCatalog={setShowCatalog}
                                                                                redirectToPlan={currentPlan == null}
                                                                            />
                                                                        ))}
                                                                <ProductCatalogPage
                                                                    source={"Homepage"}
                                                                    setShowCatalog={setShowCatalog}
                                                                    currentPlan={currentPlan}
                                                                    redirectToPlan={currentPlan == null}
                                                                    tab={activeTab.id} // ALL tab
                                                                    checkAiBundlesCreationStatus={
                                                                        checkAiBundlesCreationStatus
                                                                    }
                                                                    trackGetAIBundle={trackGetAIBundle}
                                                                    loadingAiBundles={loadingBundles}
                                                                    filteredBundleCards={filteredBundleCards}
                                                                    filteredAICards={filteredAICards}
                                                                    filteredCartCards={filteredCartCards}
                                                                />
                                                                {filteredPostPurchaseCards.length > 0 &&
                                                                    filteredPostPurchaseCards
                                                                        .slice(0, 1)
                                                                        .filter((cc) => cc?.label)
                                                                        .map((cc) => (
                                                                            <CartCard
                                                                                item={cc}
                                                                                key={cc.bundle_type}
                                                                                source={"Homepage"}
                                                                                currentPlan={currentPlan}
                                                                                redirectToPlan={currentPlan == null}
                                                                            />
                                                                        ))}

                                                                {filteredThankYouCards.length > 0 &&
                                                                    filteredThankYouCards
                                                                        .slice(0, 1)
                                                                        .filter((cc) => cc?.label)
                                                                        .map((cc) => (
                                                                            <CartCard
                                                                                item={cc}
                                                                                key={cc.bundle_type}
                                                                                source={"Homepage"}
                                                                                currentPlan={currentPlan}
                                                                                redirectToPlan={currentPlan == null}
                                                                            />
                                                                        ))}
                                                                {/* {filteredAICards?.length > 0 &&
                                                                    filteredAICards
                                                                        .slice(0, 1)
                                                                        .filter((cc) => cc?.label)
                                                                        .map((cc) => (
                                                                            <CartCard
                                                                                item={cc}
                                                                                key={cc.bundle_type}
                                                                                source={"Homepage"}
                                                                                currentPlan={currentPlan}
                                                                                redirectToPlan={currentPlan == null}
                                                                                forAi={true}
                                                                                loadingAiBundles={false}
                                                                                checkAiBundlesCreationStatus={
                                                                                    checkAiBundlesCreationStatus
                                                                                }
                                                                                trackGetAIBundle={trackGetAIBundle}
                                                                            />
                                                                        ))} */}

                                                                {/* empty cards to keep layout seamless (always 6) */}
                                                                {totalVisibleCardsCount < 6 &&
                                                                    Array.from(
                                                                        { length: 6 - totalVisibleCardsCount },
                                                                        (_, index) => (
                                                                            <div
                                                                                key={index}
                                                                                style={{ visibility: "hidden" }}
                                                                            >
                                                                                Empty
                                                                            </div>
                                                                        ),
                                                                    )}
                                                            </div>
                                                        ) : (
                                                            <div className={styles.noItemsFound}>No items found</div>
                                                        ))}

                                                    {activeTab.id === TABS[1].id && (
                                                        <div>
                                                            <div className={styles.cartCards}>
                                                                {filteredBundleBuilderCards?.filter((cc) => cc?.label)
                                                                    ?.length > 0 ? (
                                                                    filteredBundleBuilderCards.map((cc) =>
                                                                        cc?.label ? (
                                                                            <BundleCard
                                                                                item={cc}
                                                                                key={cc.bundle_type}
                                                                                source={"Homepage"}
                                                                                // currentPlan={currentPlan}
                                                                                redirectToPlan={currentPlan == null}
                                                                                setShowCatalog={setShowCatalog}
                                                                            />
                                                                        ) : (
                                                                            <div
                                                                                key={cc.bundle_type}
                                                                                style={{ visibility: "hidden" }}
                                                                            >
                                                                                Empty
                                                                            </div>
                                                                        ),
                                                                    )
                                                                ) : (
                                                                    <div className={styles.noItemsFound}>
                                                                        No items found
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {activeTab.id === TABS[2].id && (
                                                        <div className={styles.appsCard}>
                                                            {filteredBundleCards?.filter((cc) => cc?.label)?.length >
                                                            0 ? (
                                                                <ProductCatalogPage
                                                                    source={"Homepage"}
                                                                    currentPlan={currentPlan}
                                                                    setShowCatalog={setShowCatalog}
                                                                    redirectToPlan={currentPlan == null}
                                                                    tab={activeTab.id}
                                                                    filteredBundleCards={filteredBundleCards}
                                                                />
                                                            ) : (
                                                                <div className={styles.noItemsFound}>
                                                                    No items found
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                    {activeTab.id == TABS[3].id && (
                                                        <div>
                                                            <div className={`${styles.cartCards}`}>
                                                                {filteredCartCards?.filter((cc) => cc?.label)?.length >
                                                                0 ? (
                                                                    filteredCartCards.map((cc) => {
                                                                        return cc?.label ? (
                                                                            <CartCard
                                                                                item={cc}
                                                                                key={cc.bundle_type}
                                                                                source={"Homepage"}
                                                                                currentPlan={currentPlan}
                                                                                redirectToPlan={currentPlan == null}
                                                                            />
                                                                        ) : (
                                                                            <div style={{ visibility: "hidden" }}>
                                                                                Empty
                                                                            </div>
                                                                        );
                                                                    })
                                                                ) : (
                                                                    <div className={styles.noItemsFound}>
                                                                        No items found
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {activeTab.id === TABS[4].id && (
                                                        <div>
                                                            <div className={`${styles.cartCards}`}>
                                                                {filteredPostPurchaseCards?.filter((cc) => cc?.label)
                                                                    ?.length > 0 ? (
                                                                    filteredPostPurchaseCards.map((cc) => {
                                                                        return cc.label ? (
                                                                            <CartCard
                                                                                item={cc}
                                                                                key={cc.bundle_type}
                                                                                source={"Homepage"}
                                                                                currentPlan={currentPlan}
                                                                                redirectToPlan={currentPlan == null}
                                                                            />
                                                                        ) : (
                                                                            <div style={{ visibility: "hidden" }}>
                                                                                Empty
                                                                            </div>
                                                                        );
                                                                    })
                                                                ) : (
                                                                    <div className={styles.noItemsFound}>
                                                                        No items found
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {activeTab.id === TABS[5].id && (
                                                        <div>
                                                            <div className={`${styles.cartCards}`}>
                                                                {filteredThankYouCards?.filter((cc) => cc?.label)
                                                                    ?.length > 0 ? (
                                                                    filteredThankYouCards.map((cc) => {
                                                                        return cc?.label ? (
                                                                            <CartCard
                                                                                item={cc}
                                                                                key={cc.bundle_type}
                                                                                source={"Homepage"}
                                                                                currentPlan={currentPlan}
                                                                                redirectToPlan={currentPlan == null}
                                                                            />
                                                                        ) : (
                                                                            <div style={{ visibility: "hidden" }}>
                                                                                Empty
                                                                            </div>
                                                                        );
                                                                    })
                                                                ) : (
                                                                    <div className={styles.noItemsFound}>
                                                                        No items found
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            }
                        />
                    </Routes>
                </>
            </div>

            {/* <div className={styles.kefiFooter}>
                <strong className={styles.kefiFooterBrand}>Kefi Commerce</strong>
                <p className={styles.kefiFooterTagline}>Built for merchants who refuse to settle.</p>
            </div> */}
        </AppsContext.Provider>
    );
};

export default BundlesHomePage;
